export interface NamingConfig {
  pattern: string; // "{name}_{date}_{vid}"
  dateFormat: 'YYYYMMDD' | 'YYYY-MM-DD' | 'MMDDYYYY';
  separator: '_' | '-';
  videoNumberPadding: number; // e.g., 3 = "001", "002"
}

export interface FileNameParams {
  userName: string;
  uploadDate: Date;
  videoIndex: number;
  fileExtension: string;
  config: NamingConfig;
}

export class FileNamingService {
  private static instance: FileNamingService;

  private constructor() {}

  static getInstance(): FileNamingService {
    if (!FileNamingService.instance) {
      FileNamingService.instance = new FileNamingService();
    }
    return FileNamingService.instance;
  }

  sanitizeName(name: string): string {
    // Remove special characters and spaces
    return name
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim();
  }

  formatDate(date: Date, format: string): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    switch (format) {
      case 'YYYYMMDD':
        return `${year}${month}${day}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'MMDDYYYY':
        return `${month}${day}${year}`;
      default:
        return `${year}${month}${day}`;
    }
  }

  generateFootageFilename(params: FileNameParams): string {
    const {
      userName,
      uploadDate,
      videoIndex,
      fileExtension,
      config,
    } = params;

    const sanitizedName = this.sanitizeName(userName);
    const formattedDate = this.formatDate(uploadDate, config.dateFormat);
    const videoNumber = String(videoIndex).padStart(
      config.videoNumberPadding,
      '0'
    );

    const parts = config.pattern.split(/[_-]/);
    const filename = parts
      .map((part) => {
        if (part.includes('{name}')) return sanitizedName;
        if (part.includes('{date}')) return formattedDate;
        if (part.includes('{vid}')) return `vid${videoNumber}`;
        return part;
      })
      .join(config.separator);

    return `${filename}.${fileExtension}`;
  }

  parseFilename(
    filename: string,
    config: NamingConfig
  ): {
    userName: string;
    date: Date;
    videoIndex: number;
  } | null {
    try {
      const parts = filename.split(config.separator);

      if (parts.length < 3) return null;

      const userName = parts[0];
      const dateStr = parts[1];
      const vidStr = parts[2].replace('vid', '').replace(/\.\w+$/, '');

      let date: Date;
      switch (config.dateFormat) {
        case 'YYYYMMDD':
          date = new Date(
            parseInt(dateStr.substring(0, 4)),
            parseInt(dateStr.substring(4, 6)) - 1,
            parseInt(dateStr.substring(6, 8))
          );
          break;
        case 'YYYY-MM-DD':
          date = new Date(dateStr);
          break;
        case 'MMDDYYYY':
          date = new Date(
            parseInt(dateStr.substring(4, 8)),
            parseInt(dateStr.substring(0, 2)) - 1,
            parseInt(dateStr.substring(2, 4))
          );
          break;
        default:
          return null;
      }

      const videoIndex = parseInt(vidStr);

      return {
        userName,
        date,
        videoIndex,
      };
    } catch (error) {
      return null;
    }
  }

  generateFolderPath(
    companyId: string,
    userId: string,
    uploadDate: Date,
    folderStructure: 'user' | 'date' | 'location'
  ): string {
    const year = uploadDate.getFullYear();
    const month = String(uploadDate.getMonth() + 1).padStart(2, '0');
    const day = String(uploadDate.getDate()).padStart(2, '0');

    switch (folderStructure) {
      case 'user':
        return `companies/${companyId}/users/${userId}/${year}/${month}/${day}`;
      case 'date':
        return `companies/${companyId}/footage/${year}/${month}/${day}`;
      case 'location':
        return `companies/${companyId}/footage/${year}/${month}/${day}`;
      default:
        return `companies/${companyId}/users/${userId}/${year}/${month}/${day}`;
    }
  }
}

// Singleton instance
export const fileNamingService = FileNamingService.getInstance();
